// src/pages/superadmin/SuperAdminUsersPage.tsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { InlineLoader } from "components/ui/Loader";
import { getErrorMessage } from "utils/errors";
import {
  superadminSearchUsers,
  superadminGrantAdmin,
  superadminRevokeAdmin,
  UserRow,
} from "api/admin";

const PAGE_SIZE_DEFAULT = 25;

type LineGeom = {
  left: number;
  titleTop: number;
  titleBottom: number;
  containerTop: number;
  containerBottom: number;
};

const SuperAdminUsersPage: React.FC = () => {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE_DEFAULT);
  const [items, setItems] = useState<UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [granting, setGranting] = useState(false);
  const [grantEmail, setGrantEmail] = useState("");

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  // Title/container vertical line geometry (CookiesPage logic)
  const pageRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [geom, setGeom] = useState<LineGeom | null>(null);

  const load = async (opts?: { forcePage?: number }) => {
    const nextPage = opts?.forcePage ?? page;
    setErr("");
    setLoading(true);
    try {
      const res = await superadminSearchUsers(q.trim(), nextPage, pageSize);
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e: any) {
      setErr(getErrorMessage(e, "Failed to load users"));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  useLayoutEffect(() => {
    const pageEl = pageRef.current;
    const titleEl = titleRef.current;
    const containerEl = containerRef.current;
    if (!pageEl || !titleEl || !containerEl) return;

    const compute = () => {
      const p = pageEl.getBoundingClientRect();
      const t = titleEl.getBoundingClientRect();
      const c = containerEl.getBoundingClientRect();

      const left = Math.round(t.left + t.width / 2 - p.left);

      setGeom({
        left: Math.max(0, left),
        titleTop: Math.max(0, Math.round(t.top - p.top)),
        titleBottom: Math.max(0, Math.round(t.bottom - p.top)),
        containerTop: Math.max(0, Math.round(c.top - p.top)),
        containerBottom: Math.max(0, Math.round(c.bottom - p.top)),
      });
    };

    compute();

    const ro = new ResizeObserver(() => compute());
    ro.observe(pageEl);
    ro.observe(titleEl);
    ro.observe(containerEl);

    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [items.length, total, loading, err]);

  const onSearch = async () => {
    setPage(1);
    await load({ forcePage: 1 });
  };

  const onGrant = async () => {
    const email = grantEmail.trim();
    if (!email) return;
    setGranting(true);
    setErr("");
    try {
      const res = await superadminGrantAdmin(email);
      setItems((prev) => prev.map((u) => (u.id === res.user_id ? { ...u, is_admin: true } : u)));
      setGrantEmail("");
    } catch (e: any) {
      setErr(getErrorMessage(e, "Failed to grant admin"));
    } finally {
      setGranting(false);
    }
  };

  const onRevoke = async (userId: number) => {
    setErr("");
    try {
      await superadminRevokeAdmin(userId);
      setItems((prev) => prev.map((u) => (u.id === userId ? { ...u, is_admin: false } : u)));
    } catch (e: any) {
      setErr(getErrorMessage(e, "Failed to revoke admin"));
    }
  };

  const TITLE_GAP_PX = 14;
  const CONTAINER_TOP_MARGIN_CLASS = "mt-16 sm:mt-20";

  return (
    <main ref={pageRef} className="relative min-h-screen px-4 py-12 sm:py-16">
      {/* Vertical gold line segments (with gaps for title + container) */}
      {geom && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: 0,
              bottom: `calc(100% - ${Math.max(0, geom.titleTop - TITLE_GAP_PX)}px)`,
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: geom.titleBottom + TITLE_GAP_PX,
              bottom: `calc(100% - ${geom.containerTop}px)`,
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: geom.containerBottom,
              bottom: 0,
            }}
          />
        </>
      )}

      <div className="relative z-10">
        {/* Title */}
        <header className="text-center">
          <h1
            ref={titleRef}
            className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#ceddf2]"
          >
            Superadmin · Manage admins
          </h1>
        </header>

        {/* Main container */}
        <section className={[CONTAINER_TOP_MARGIN_CLASS, "flex justify-center"].join(" ")}>
          <div
            ref={containerRef}
            className="w-full md:w-3/5 bg-white rounded-2xl shadow-sm border border-slate-200 px-6 py-8 sm:px-8"
          >
            <div className="flex items-start justify-between gap-4">
              <Link
                to="/admin"
                className="font-ui text-xs uppercase tracking-wide text-slate-500 hover:opacity-80"
              >
                ← Back to Admin Home
              </Link>

              <button
                type="button"
                onClick={() => load()}
                className={[
                  "inline-flex items-center justify-center rounded-full",
                  "border border-slate-200 bg-white",
                  "px-4 py-2",
                  "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                  "hover:bg-slate-50 transition-colors font-ui font-bold",
                ].join(" ")}
              >
                Refresh
              </button>
            </div>

            {err && (
              <div className="mt-6 border border-red-300 bg-red-50 text-red-700 p-3 rounded">
                {err}
              </div>
            )}

            {/* Search */}
            <section className="mt-8 rounded-2xl border border-slate-200 p-5 space-y-3">
              <div className="font-ui font-bold uppercase tracking-[0.18em] text-[#e22e5a] text-xs">
                Search users
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  className="border border-slate-200 rounded-xl px-4 py-3 flex-1 font-body focus:outline-none focus:ring"
                  placeholder="Search by email or display name"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                <button
                  onClick={onSearch}
                  className={[
                    "inline-flex items-center justify-center rounded-full",
                    "bg-[#f3dfae] px-6 py-3",
                    "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                    "hover:opacity-90 transition-opacity font-ui font-bold",
                  ].join(" ")}
                >
                  Search →
                </button>
              </div>

              <div className="font-ui text-xs uppercase tracking-wide text-slate-500">
                Showing page {page} of {totalPages} · total {total}
              </div>
            </section>

            {/* Grant */}
            <section className="mt-6 rounded-2xl border border-slate-200 p-5 space-y-3">
              <div className="font-ui font-bold uppercase tracking-[0.18em] text-[#e22e5a] text-xs">
                Grant admin
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  className="border border-slate-200 rounded-xl px-4 py-3 flex-1 font-body focus:outline-none focus:ring"
                  type="email"
                  placeholder="user@example.com"
                  value={grantEmail}
                  onChange={(e) => setGrantEmail(e.target.value)}
                />
                <button
                  onClick={onGrant}
                  disabled={granting || !grantEmail.trim()}
                  className={[
                    "inline-flex items-center justify-center rounded-full",
                    "bg-[#f3dfae] px-6 py-3",
                    "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                    "hover:opacity-90 transition-opacity font-ui font-bold",
                    "disabled:opacity-50 disabled:pointer-events-none",
                  ].join(" ")}
                >
                  {granting ? "Granting…" : "Grant →"}
                </button>
              </div>

              <div className="font-body text-sm text-slate-600">
                Grants the <span className="font-semibold">admin</span> role to an existing user by
                email.
              </div>
            </section>

            {/* Table */}
            <section className="mt-8">
              <div className="font-ui font-bold uppercase tracking-[0.18em] text-slate-800 text-xs mb-3">
                Users
              </div>

              {loading ? (
                <InlineLoader label="Loading…" />
              ) : items.length === 0 ? (
                <div className="font-body text-slate-600">No results.</div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-slate-700 font-ui text-xs uppercase tracking-wide">
                        <th className="text-left p-3">Email</th>
                        <th className="text-left p-3">Display</th>
                        <th className="text-left p-3">Admin</th>
                        <th className="text-left p-3">Superadmin</th>
                        <th className="text-left p-3">Created</th>
                        <th className="text-left p-3">Last Login</th>
                        <th className="text-left p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((u) => (
                        <tr key={u.id} className="border-t border-slate-200">
                          <td className="p-3 font-body text-slate-900">{u.email}</td>
                          <td className="p-3 font-body text-slate-700">{u.display_name || "—"}</td>
                          <td className="p-3 font-body text-slate-700">{u.is_admin ? "Yes" : "No"}</td>
                          <td className="p-3 font-body text-slate-700">
                            {u.is_superadmin ? "Yes" : "No"}
                          </td>
                          <td className="p-3 font-body text-slate-700">
                            {u.created_date ? new Date(u.created_date).toLocaleString() : "—"}
                          </td>
                          <td className="p-3 font-body text-slate-700">
                            {u.last_login_date ? new Date(u.last_login_date).toLocaleString() : "—"}
                          </td>
                          <td className="p-3">
                            {u.is_admin ? (
                              <button
                                onClick={() => onRevoke(u.id)}
                                className={[
                                  "inline-flex items-center justify-center rounded-full",
                                  "border border-slate-200 bg-white px-4 py-2",
                                  "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                                  "hover:bg-slate-50 transition-colors font-ui font-bold",
                                ].join(" ")}
                              >
                                Revoke
                              </button>
                            ) : (
                              <button
                                onClick={async () => {
                                  setErr("");
                                  try {
                                    const res = await superadminGrantAdmin(u.email);
                                    setItems((prev) =>
                                      prev.map((x) =>
                                        x.id === res.user_id ? { ...x, is_admin: true } : x
                                      )
                                    );
                                  } catch (e: any) {
                                    setErr(getErrorMessage(e, "Failed to grant"));
                                  }
                                }}
                                className={[
                                  "inline-flex items-center justify-center rounded-full",
                                  "bg-[#f3dfae] px-4 py-2",
                                  "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                                  "hover:opacity-90 transition-opacity font-ui font-bold",
                                ].join(" ")}
                              >
                                Grant →
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex items-center justify-between gap-3 mt-4">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={[
                      "inline-flex items-center justify-center rounded-full",
                      "border border-slate-200 bg-white px-4 py-2",
                      "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                      "hover:bg-slate-50 transition-colors font-ui font-bold",
                      "disabled:opacity-50 disabled:pointer-events-none",
                    ].join(" ")}
                  >
                    Prev
                  </button>

                  <div className="font-ui text-xs uppercase tracking-wide text-slate-500">
                    Page {page} / {totalPages}
                  </div>

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={[
                      "inline-flex items-center justify-center rounded-full",
                      "border border-slate-200 bg-white px-4 py-2",
                      "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                      "hover:bg-slate-50 transition-colors font-ui font-bold",
                      "disabled:opacity-50 disabled:pointer-events-none",
                    ].join(" ")}
                  >
                    Next
                  </button>
                </div>
              )}
            </section>
          </div>
        </section>
      </div>
    </main>
  );
};

export default SuperAdminUsersPage;
